import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import PageTransition from './PageTransition';
import AnnouncementHtml from './AnnouncementHtml';
import { useMenu } from '../contexts/MenuContext';
import { useReadAnnouncements } from '../hooks/useReadAnnouncements';
import { useRipple } from '../hooks/useRipple';
import { getAnnouncements, markAllAnnouncementsRead, markAnnouncementRead } from '../services/announcementService';
import { Announcement } from '../types/Announcement';
import { announcementCardParts, announcementIds, downloadAnnouncementFile, formatAnnouncementDate, prepareAnnouncementHtml } from '../utils/announcement';
import { useAuth } from '../contexts/AuthContext';
import AnnouncementDetailModal from './AnnouncementDetailModal';

type AnnouncementFeedProps = {
  title: string;
  description: string;
  emptyTitle: string;
  emptyText: string;
  errorTitle: string;
  iconPath: string;
  filter?: (announcement: Announcement) => boolean;
  downloadOnClick?: boolean;
};

export default function AnnouncementFeed({
  title,
  description,
  emptyTitle,
  emptyText,
  errorTitle,
  iconPath,
  filter,
  downloadOnClick = false,
}: AnnouncementFeedProps) {
  const { isMenuButtonVisible } = useMenu();
  const { userData } = useAuth();
  const canMarkAll = userData?.MarkAllNotificationsAsRead === true;
  const queryClient = useQueryClient();
  const readIds = useReadAnnouncements();
  const { ripples, createRipple } = useRipple<number>();
  const [markingId, setMarkingId] = useState<number | null>(null);
  const [markErrorId, setMarkErrorId] = useState<number | null>(null);
  const [openAnnouncement, setOpenAnnouncement] = useState<Announcement | null>(null);

  const {
    data: announcements = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['announcements'],
    queryFn: getAnnouncements,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const visible = filter ? announcements.filter(filter) : announcements;

  const confirmRead = async (id: number, markAll = false) => {
    if (markingId !== null) return;

    setMarkingId(id);
    setMarkErrorId(null);

    try {
      if (canMarkAll && markAll) {
        const cached = queryClient.getQueryData<Announcement[]>(['announcements']);
        await markAllAnnouncementsRead(announcementIds(cached, id));
        queryClient.setQueryData(['priority-announcement'], null);
      } else {
        await markAnnouncementRead(id);
        queryClient.setQueryData(['priority-announcement'], (current: { Id?: number } | null | undefined) =>
          current?.Id === id ? null : current
        );
        await queryClient.invalidateQueries({ queryKey: ['priority-announcement'] });
      }
    } catch (error) {
      console.error('Duyuru okundu işaretlenemedi:', error);
      setMarkErrorId(id);
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto min-h-screen min-w-0">
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={iconPath} />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    {title}
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    {description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg">
          <div className="rounded-xl p-6 border border-white dark:border-gray-700">
            {isLoading && (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            )}

            {!isLoading && isError && (
              <div className="text-center py-12">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {errorTitle}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
                  Liste alınırken bir sorun oluştu. Bağlantınızı kontrol edip yeniden deneyin.
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-blue-600 hover:to-indigo-700"
                >
                  Yeniden dene
                </button>
              </div>
            )}

            {!isLoading && !isError && visible.length === 0 && (
              <div className="text-center py-12">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 rounded-2xl shadow-lg border border-indigo-100 dark:border-gray-600">
                    <div className="rounded-2xl w-full h-full flex items-center justify-center border border-white dark:border-gray-700">
                      <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={iconPath} />
                      </svg>
                    </div>
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {emptyTitle}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                  {emptyText}
                </p>
              </div>
            )}

            {!isLoading && !isError && visible.length > 0 && (
              <div className="space-y-4">
                {visible.map((announcement, index) => {
                  const id = announcement.Id as number;
                  const isRead = readIds.includes(id);
                  const card = downloadOnClick ? { text: '', linkHtml: '' } : announcementCardParts(announcement);
                  const canOpen = !downloadOnClick && (card.text.length > 0 || card.linkHtml.length > 0 || prepareAnnouncementHtml(announcement.Duyuru) !== '');

                  return (
                    <motion.article
                      key={id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index, 8) * 0.04 }}
                      role={downloadOnClick || canOpen ? 'button' : undefined}
                      tabIndex={downloadOnClick || canOpen ? 0 : undefined}
                      aria-label={downloadOnClick ? `${announcement.Baslik} dosyasını indir` : canOpen ? `${announcement.Baslik} duyurusunu aç` : undefined}
                      onClick={downloadOnClick && announcement.DosyaRumuz ? (event) => {
                        if ((event.target as HTMLElement).closest('a, button')) return;
                        const rumuz = announcement.DosyaRumuz as string;
                        createRipple(event, id, () => downloadAnnouncementFile(rumuz));
                      } : canOpen ? (event) => {
                        if ((event.target as HTMLElement).closest('a, button')) return;
                        createRipple(event, id, () => setOpenAnnouncement(announcement));
                      } : undefined}
                      onKeyDown={downloadOnClick && announcement.DosyaRumuz ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        downloadAnnouncementFile(announcement.DosyaRumuz as string);
                      } : canOpen ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        setOpenAnnouncement(announcement);
                      } : undefined}
                      whileTap={downloadOnClick || canOpen ? { scale: 0.98 } : undefined}
                      className={`relative min-w-0 max-w-full border border-gray-200 dark:border-gray-600 rounded-xl bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700/50 dark:via-gray-700/40 dark:to-gray-600/50 overflow-hidden ${(downloadOnClick || canOpen) ? 'cursor-pointer hover:shadow-md hover:from-white hover:via-gray-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:via-gray-700/90 dark:hover:to-gray-600 transition-all duration-200' : ''}`}
                    >
                      <div className="min-w-0 rounded-xl p-5 border border-white dark:border-gray-700">
                        {(downloadOnClick || canOpen) && ripples[id]?.map((ripple) => (
                          <motion.div
                            key={ripple.id}
                            className="absolute rounded-full bg-indigo-400/30 pointer-events-none"
                            style={{
                              left: ripple.x - (ripple.size || 50) / 2,
                              top: ripple.y - (ripple.size || 50) / 2,
                              width: ripple.size || 50,
                              height: ripple.size || 50,
                            }}
                            initial={{ scale: 0.1, opacity: 0.6 }}
                            animate={{ scale: 1, opacity: 0 }}
                            transition={{ duration: 0.6, ease: 'easeOut' }}
                          />
                        ))}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatAnnouncementDate(announcement.Tarih)}
                          </p>
                          {isRead && (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                              Okundu
                            </span>
                          )}
                        </div>

                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {announcement.Baslik}
                        </h2>

                        {downloadOnClick ? (
                          <AnnouncementHtml html={announcement.Duyuru} className="mt-3 min-w-0" omitDownloadLink />
                        ) : (
                          <>
                            {card.text && (
                              <p className="mt-3 min-w-0 truncate text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                                {card.text}
                              </p>
                            )}
                            {card.linkHtml && (
                              <div
                                className={`${card.text ? 'mt-2' : 'mt-3'} min-w-0 truncate text-sm leading-relaxed [&_a]:!text-blue-700 dark:[&_a]:!text-blue-300 [&_a]:!underline`}
                                dangerouslySetInnerHTML={{ __html: card.linkHtml }}
                              />
                            )}
                          </>
                        )}

                        {!isRead && (
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => confirmRead(id)}
                              disabled={markingId !== null}
                              className="rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-blue-600 hover:to-indigo-700 disabled:opacity-60"
                            >
                              {markingId === id ? 'İşaretleniyor...' : 'Okudum'}
                            </button>
                            {markErrorId === id && (
                              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                Okundu işaretlenemedi. Tekrar deneyin.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <AnnouncementDetailModal
        announcement={openAnnouncement}
        isRead={openAnnouncement?.Id != null && readIds.includes(openAnnouncement.Id)}
        canMarkAll={canMarkAll}
        marking={openAnnouncement?.Id != null && markingId === openAnnouncement.Id}
        markError={openAnnouncement?.Id != null && markErrorId === openAnnouncement.Id}
        onClose={() => setOpenAnnouncement(null)}
        onConfirmRead={confirmRead}
      />
    </PageTransition>
  );
}
